import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { darkenHexColor, lightenHexColor } from "../../utilities/parsers";

// All measurements below are in the SVG's own design-space units (the
// original hand-drawn artwork was authored on a 360x150 canvas with an
// 18px label). Everything scales off of that reference.
const BASE_FONT_SIZE = 18;
const DESIGN_WIDTH = 360;
const DESIGN_HEIGHT = 150;
const TEXT_CENTER_X = 180;
const CONTENT_TOP = 29;

// The label reads small at the badge's native display size, so its glyphs are
// drawn boosted relative to BASE_FONT_SIZE - this only affects the text, the
// ribbon/bat artwork keeps scaling strictly off BASE_FONT_SIZE.
const TEXT_SIZE_BOOST = 1.35;
const TEXT_FONT_SIZE = BASE_FONT_SIZE * TEXT_SIZE_BOOST;

// How much horizontal room the ribbon body offers a label before it needs
// to stretch, and how much breathing room to keep around the measured text.
const TEXT_SAFE_WIDTH = 190;
const SIDE_PADDING = 14;

// The badge only ever grows/shrinks in exact proportion to the configured
// font size (never distorts), clamped so a stray value can't blow up the layout.
const MIN_FONT_SCALE = 0.5;
const MAX_FONT_SCALE = 2.5;

// Native display size of the artwork at BASE_FONT_SIZE, before font scaling.
const DISPLAY_SCALE = 0.35;

// The whole ribbon is built from exactly two seed colors - every other tone
// (gradient stops, strokes, shadows) is derived from these via lighten/darken
// so the artwork stays a coherent 2-color palette.
const DEFAULT_PRIMARY_COLOR = "#111111";
const DEFAULT_SECONDARY_COLOR = "#f36a0a";

const estimateTextWidth = (text: string) => text.length * TEXT_FONT_SIZE * 0.55;

type DiscountRibbonBadgeProps = {
    text: string;
    fontSize?: number;
    fontWeight?: string | number;
    fontStyle?: string;
    color?: string;
    primaryColor?: string;
    secondaryColor?: string;
    style?: CSSProperties;
    [dataAttr: `data-${string}`]: unknown;
};

const DiscountRibbonBadge = ({
    text,
    fontSize,
    fontWeight,
    fontStyle,
    color,
    primaryColor,
    secondaryColor,
    style,
    ...rest
}: DiscountRibbonBadgeProps) => {
    const uid = useId();
    const textRef = useRef<SVGTextElement>(null);
    const [textWidth, setTextWidth] = useState(() => estimateTextWidth(text || ""));

    const measure = () => {
        const node = textRef.current;
        if (!node) return;
        try {
            const box = node.getBBox();
            if (box.width > 0) setTextWidth(box.width);
        } catch {
            // node not measurable yet (e.g. not laid out) - keep last known width
        }
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    useEffect(() => {
        const fonts = (document as any)?.fonts;
        if (fonts?.ready) {
            fonts.ready.then(measure).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    if (!text) return null;

    const safeFontSize = fontSize && fontSize > 0 ? fontSize : BASE_FONT_SIZE;
    const fontScale = Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, safeFontSize / BASE_FONT_SIZE));

    const delta = Math.max(0, textWidth + SIDE_PADDING * 2 - TEXT_SAFE_WIDTH);
    const designWidth = DESIGN_WIDTH + delta;

    const finalScale = DISPLAY_SCALE * fontScale;
    const renderedWidth = designWidth * finalScale;
    const renderedHeight = DESIGN_HEIGHT * finalScale;
    const textX = TEXT_CENTER_X + delta / 2;

    const shadowId = `hsr-shadow-${uid}`;
    const orangeId = `ribbon-orange-${uid}`;
    const blackId = `black-ribbon-${uid}`;

    const primary = primaryColor || DEFAULT_PRIMARY_COLOR;
    const secondary = secondaryColor || DEFAULT_SECONDARY_COLOR;

    // Neutral family: bats, ribbon folds, the ribbon's own shadow/stroke, and the spider.
    const primaryLight = lightenHexColor(primary, 8);
    const primaryDark = darkenHexColor(primary, 4);

    // Accent family: bat veins, the ribbon's orange gradient, borders, and the underline.
    const secondaryLight = lightenHexColor(secondary, 20);
    const secondaryDark = darkenHexColor(secondary, 15);

    return (
        <svg
            {...rest}
            width={renderedWidth}
            height={renderedHeight}
            viewBox={`0 0 ${designWidth} ${DESIGN_HEIGHT}`}
            style={{
                position: "absolute",
                top: -((CONTENT_TOP / DESIGN_HEIGHT) * renderedHeight + 18),
                right: -6,
                overflow: "visible",
                zIndex: 10,
                ...style,
            }}
            role="img"
            aria-label={text}
        >
            <defs>
                <filter id={shadowId} x="-20%" y="-30%" width="140%" height="180%">
                    <feDropShadow dx="0" dy="5" stdDeviation="3" floodColor="#000" floodOpacity=".5" />
                </filter>
                <linearGradient id={orangeId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={secondaryLight} />
                    <stop offset="45%" stopColor={secondary} />
                    <stop offset="100%" stopColor={secondaryDark} />
                </linearGradient>
                <linearGradient id={blackId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={primaryLight} />
                    <stop offset="50%" stopColor={primary} />
                    <stop offset="100%" stopColor={primaryDark} />
                </linearGradient>
            </defs>

            {/* Left ornament (bat + ribbon fold + web + spider): fixed size, anchored to the left cap */}
            <path
                d="M82 57C67 39 49 31 26 32c8 8 12 16 12 24-8-3-17-3-27 1 11 6 18 14 20 25 10-5 20-6 30-3 4 8 10 13 18 16l11-20z"
                fill={primaryDark}
                stroke={primaryLight}
                strokeWidth={2}
            />
            <path d="M29 35c16 0 30 8 43 23" fill="none" stroke={secondaryLight} strokeWidth={2} opacity={0.55} />
            <path d="M67 42L83 52L82 108L70 116Z" fill={primaryDark} stroke={primaryLight} strokeWidth={2} />

            {/* Right ornament: exact mirror of the left, rigidly translated as the ribbon stretches */}
            <g transform={`translate(${delta} 0)`}>
                <path
                    d="M278 57c15-18 33-26 56-25-8 8-12 16-12 24 8-3 17-3 27 1 -11 6-18 14-20 25-10-5-20-6-30-3-4 8-10 13-18 16l-11-20z"
                    fill={primaryDark}
                    stroke={primaryLight}
                    strokeWidth={2}
                />
                <path d="M331 35c-16 0-30 8-43 23" fill="none" stroke={secondaryLight} strokeWidth={2} opacity={0.55} />
                <path d="M293 37L277 50L278 108L290 116Z" fill={primaryDark} stroke={primaryLight} strokeWidth={2} />
            </g>

            {/* Ribbon body: the only part that stretches to fit the label */}
            <path d={`M72 58H${288 + delta}L${278 + delta} 121H82Z`} fill={primaryDark} filter={`url(#${shadowId})`} />
            <path
                d={`M67 34L${293 + delta} 29L${283 + delta} 98L76 103Z`}
                fill={`url(#${orangeId})`}
                stroke={primaryDark}
                strokeWidth={3}
                filter={`url(#${shadowId})`}
            />
            <path
                d={`M73 43L${286 + delta} 38L${279 + delta} 91L80 96Z`}
                fill={`url(#${blackId})`}
                stroke={secondary}
                strokeWidth={3}
            />
            <path
                d={`M78 48L${281 + delta} 43L${277 + delta} 86L82 91Z`}
                fill="none"
                stroke={secondaryLight}
                strokeWidth={1}
                opacity={0.5}
            />
            <path d={`M75 38L${287 + delta} 33L${285 + delta} 38L74 43Z`} fill="#fff" opacity={0.13} />

            <g fill="none" stroke="#fff" strokeWidth={1.2} opacity={0.8}>
                <path d="M78 45L110 84" />
                <path d="M78 45L92 91" />
                <path d="M78 45L78 94" />
                <path d="M78 45L67 91" />
                <path d="M78 45L55 80" />
                <path d="M68 53C73 51 82 51 88 55C91 58 92 62 92 66" />
                <path d="M62 60C70 56 83 56 91 62C96 66 97 72 97 78" />
                <path d="M57 68C68 62 84 62 94 69C101 74 103 82 102 88" />
                <path d="M52 77C65 69 85 69 97 77C104 82 108 88 108 94" />
            </g>
            <g transform="translate(91 50)" fill={primaryDark} stroke={primaryDark} strokeWidth={1.5}>
                <ellipse cx="0" cy="0" rx="4" ry="5" />
                <circle cx="0" cy="-6" r="2.5" />
                <path d="M-3-2L-9-7L-13-6" />
                <path d="M-4 0L-11-1L-15 3" />
                <path d="M-3 2L-9 6L-13 9" />
                <path d="M3-2L9-7L13-6" />
                <path d="M4 0L11-1L15 3" />
                <path d="M3 2L9 6L13 9" />
            </g>

            <text
                ref={textRef}
                x={textX}
                y={69}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color || "#ffffff"}
                fontSize={TEXT_FONT_SIZE}
                fontWeight={fontWeight || 700}
                fontStyle={fontStyle || "normal"}
                letterSpacing={1}
            >
                {text}
            </text>

            <path d={`M86 98L${276 + delta} 93`} fill="none" stroke={secondaryLight} strokeWidth={2} opacity={0.7} />
        </svg>
    );
};

export default DiscountRibbonBadge;
